import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { Plus, Trash2, Upload, Download, AlertCircle, X, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { uploadAPI } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const emptyItem = {
  title: '',
  description: '',
  price: '',
  category: '',
  condition: '',
  ageGroup: '',
  brand: '',
  color: '',
  packageDimensions: {
    weight: '',
    length: '',
    width: '',
    height: '',
  },
  images: [], // { file, preview }
};

const BulkCreateListings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (user?.accountType !== 'business' && !user?.isAdmin) {
    return (
      <div className="container" style={{ paddingTop: '40px', maxWidth: '600px' }}>
        <div className="card">
          <h2>Business Account Required</h2>
          <p className="text-muted">
            Only business accounts can access bulk listing tools.
          </p>
          <button onClick={() => navigate('/business/apply')} className="btn btn-primary" style={{ marginTop: '16px' }}>
            Apply for Business Account
          </button>
        </div>
      </div>
    );
  }

  const handleAddItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems.length > 0 ? newItems : [{ ...emptyItem }]);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newItems[index][parent] = {
        ...newItems[index][parent],
        [child]: value,
      };
    } else {
      newItems[index][field] = value;
    }
    setItems(newItems);
  };

  const handleAddImages = (index, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setItems((prev) => {
      const newItems = [...prev];
      const existing = newItems[index].images || [];
      const room = Math.max(0, 5 - existing.length); // max 5 images per item
      const toAdd = files.slice(0, room).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));
      newItems[index] = { ...newItems[index], images: [...existing, ...toAdd] };
      return newItems;
    });
  };

  const handleMoveImage = (index, imgIndex, dir) => {
    setItems((prev) => {
      const target = imgIndex + dir;
      const imgs = [...(prev[index].images || [])];
      if (target < 0 || target >= imgs.length) return prev;
      [imgs[imgIndex], imgs[target]] = [imgs[target], imgs[imgIndex]];
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], images: imgs };
      return newItems;
    });
  };

  const handleRemoveImage = (index, imgIndex) => {
    setItems((prev) => {
      const newItems = [...prev];
      const imgs = [...(newItems[index].images || [])];
      const [removed] = imgs.splice(imgIndex, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      newItems[index] = { ...newItems[index], images: imgs };
      return newItems;
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const parsedItems = jsonData.map((row) => ({
          title: row.Title || row.title || '',
          description: row.Description || row.description || '',
          price: row.Price || row.price || '',
          category: row.Category || row.category || '',
          condition: row.Condition || row.condition || '',
          ageGroup: row['Age Group'] || row.ageGroup || '',
          brand: row.Brand || row.brand || '',
          color: row.Color || row.color || '',
          packageDimensions: {
            weight: row['Weight (lbs)'] || row.weight || '',
            length: row['Length (in)'] || row.length || '',
            width: row['Width (in)'] || row.width || '',
            height: row['Height (in)'] || row.height || '',
          },
          images: [],
        }));

        setItems(parsedItems);
        setSuccess(`Imported ${parsedItems.length} items successfully!`);
      } catch (err) {
        setError('Failed to parse file. Please check the format.');
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const template = [
      {
        Title: 'Example Baby Stroller',
        Description: 'Gently used stroller in excellent condition',
        Price: 150,
        Category: 'Strollers & Car Seats',
        Condition: 'Like New',
        'Age Group': 'Infant (3-12 months)',
        Brand: 'Example Brand',
        Color: 'Blue',
        'Weight (lbs)': 15,
        'Length (in)': 30,
        'Width (in)': 20,
        'Height (in)': 40,
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, 'bulk_listing_template.xlsx');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate all items
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.title || !item.price || !item.category || !item.condition) {
        setError(`Item ${i + 1}: Please fill in all required fields (Title, Price, Category, Condition)`);
        setLoading(false);
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');

      // Upload each item's images to Cloudinary, then build its payload.
      const payloads = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const uploadedImages = [];

        const imgs = item.images || [];
        for (let j = 0; j < imgs.length; j++) {
          const img = imgs[j];
          if (img.uploaded) {
            uploadedImages.push(img.uploaded);
            continue;
          }
          setSuccess(`Uploading images… (item ${i + 1} of ${items.length})`);
          const fd = new FormData();
          fd.append('image', img.file);
          fd.append('isPrimary', uploadedImages.length === 0 ? 'true' : 'false');
          const res = await uploadAPI.uploadImage(fd);
          if (res.data?.success) {
            uploadedImages.push({
              fullSize: res.data.data.fullSize,
              thumbnail: res.data.data.thumbnail,
              isPrimary: uploadedImages.length === 0,
            });
          }
        }

        const payload = {
          title: item.title,
          description: item.description,
          price: parseFloat(item.price),
          category: item.category,
          condition: item.condition,
          ageGroup: item.ageGroup,
          brand: item.brand,
          color: item.color,
          // Items with photos go live; ones without stay drafts.
          status: uploadedImages.length > 0 ? 'active' : 'draft',
          images: uploadedImages,
          packageDimensions: {
            weight: parseFloat(item.packageDimensions.weight) || 0,
            length: parseFloat(item.packageDimensions.length) || 0,
            width: parseFloat(item.packageDimensions.width) || 0,
            height: parseFloat(item.packageDimensions.height) || 0,
          },
        };

        // Remove empty optional fields so they don't fail enum validation
        // (an empty string is not a valid enum value in Mongoose).
        if (!payload.ageGroup) delete payload.ageGroup;
        if (!payload.brand) delete payload.brand;
        if (!payload.color) delete payload.color;
        if (!payload.description) delete payload.description;

        payloads.push(payload);
      }

      setSuccess('Creating your listings…');
      const response = await axios.post(
        `${API_URL}/business/items/bulk`,
        { items: payloads },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const withImages = payloads.filter((p) => p.images.length > 0).length;
      const drafts = payloads.length - withImages;
      setSuccess(
        `Created ${response.data.count} items` +
          (withImages ? ` — ${withImages} published live` : '') +
          (drafts ? `, ${drafts} saved as drafts (add photos to publish)` : '') +
          '. Redirecting…'
      );
      setTimeout(() => navigate('/seller-dashboard'), 2200);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create items');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '40px', maxWidth: '1200px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <h1>Bulk Create Listings</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={downloadTemplate} className="btn btn-outline-primary">
            <Download size={16} style={{ marginRight: '4px' }} />
            Download Template
          </button>
          <label className="btn btn-outline-primary" style={{ margin: 0 }}>
            <Upload size={16} style={{ marginRight: '4px' }} />
            Import Excel/CSV
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px', backgroundColor: '#EFF6FF', borderLeft: '4px solid var(--primary)' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
          <AlertCircle size={20} color="var(--primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ fontSize: '14px' }}>
            <strong>Tip:</strong> Import your spreadsheet, then drag or add photos to each item below. Items with photos publish live immediately; any left without photos are saved as drafts you can finish later.
          </div>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '20px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '20px' }}>{success}</div>}

      <form onSubmit={handleSubmit}>
        {items.map((item, index) => (
          <div key={index} className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Item {index + 1}</h3>
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="btn"
                  style={{ color: '#EF4444', borderColor: '#EF4444' }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handleItemChange(index, 'title', e.target.value)}
                  className="input"
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={item.price}
                  onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                  className="input"
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Category *
                </label>
                <select
                  value={item.category}
                  onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Clothes & Shoes">Clothes & Shoes</option>
                  <option value="Toys & Games">Toys & Games</option>
                  <option value="Feeding">Feeding</option>
                  <option value="Feeding (Non-liquid, Non-edible)">Feeding (Non-liquid, Non-edible)</option>
                  <option value="Diapering">Diapering</option>
                  <option value="Bathing & Skincare">Bathing & Skincare</option>
                  <option value="Bathing (Non-liquid)">Bathing (Non-liquid)</option>
                  <option value="Health & Safety">Health & Safety</option>
                  <option value="Nursery">Nursery</option>
                  <option value="Strollers & Car Seats">Strollers & Car Seats</option>
                  <option value="Carriers & Wraps">Carriers & Wraps</option>
                  <option value="Activity & Entertainment">Activity & Entertainment</option>
                  <option value="Books">Books</option>
                  <option value="Blankets & Bedding">Blankets & Bedding</option>
                  <option value="Baby Gear">Baby Gear</option>
                  <option value="Maternity">Maternity</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Condition *
                </label>
                <select
                  value={item.condition}
                  onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Select Condition</option>
                  <option value="New with Tags">New with Tags</option>
                  <option value="Like New">Like New</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Well Loved">Well Loved</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Age Group
                </label>
                <select
                  value={item.ageGroup}
                  onChange={(e) => handleItemChange(index, 'ageGroup', e.target.value)}
                  className="input"
                >
                  <option value="">Select Age Group</option>
                  <option value="Newborn (0-3 months)">Newborn (0-3 months)</option>
                  <option value="Infant (3-12 months)">Infant (3-12 months)</option>
                  <option value="Toddler (1-3 years)">Toddler (1-3 years)</option>
                  <option value="Preschool (3-5 years)">Preschool (3-5 years)</option>
                  <option value="All Ages">All Ages</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Brand
                </label>
                <input
                  type="text"
                  value={item.brand}
                  onChange={(e) => handleItemChange(index, 'brand', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Color
                </label>
                <input
                  type="text"
                  value={item.color}
                  onChange={(e) => handleItemChange(index, 'color', e.target.value)}
                  className="input"
                />
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={item.description}
                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                className="input"
                rows="3"
              />
            </div>

            <div style={{ marginTop: '16px' }}>
              <h4 style={{ marginBottom: '8px' }}>Photos</h4>
              <p style={{ fontSize: '13px', color: 'var(--textSecondary)', margin: '0 0 12px 0' }}>
                Add up to 5 photos. Use the arrows to reorder — the first photo ("Main") is the cover. Items with photos publish live; items left without photos are saved as drafts.
              </p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                {(item.images || []).map((img, imgIndex) => (
                  <div key={imgIndex} style={{ position: 'relative', width: '80px', height: '80px' }}>
                    <img
                      src={img.preview || img.uploaded?.thumbnail}
                      alt={item.title || `Item ${index + 1}`}
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)' }}
                    />
                    {imgIndex === 0 && (
                      <span style={{ position: 'absolute', top: '2px', left: '2px', background: 'var(--primary)', color: '#fff', fontSize: '9px', padding: '1px 4px', borderRadius: '4px' }}>
                        Main
                      </span>
                    )}
                    {(item.images || []).length > 1 && (
                      <div style={{ position: 'absolute', bottom: '2px', left: '2px', right: '2px', display: 'flex', justifyContent: 'space-between' }}>
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, imgIndex, -1)}
                          disabled={imgIndex === 0}
                          aria-label="Move photo left"
                          style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '4px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: imgIndex === 0 ? 'default' : 'pointer', opacity: imgIndex === 0 ? 0.3 : 1 }}
                        >
                          <ChevronLeft size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, imgIndex, 1)}
                          disabled={imgIndex === (item.images.length - 1)}
                          aria-label="Move photo right"
                          style={{ background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '4px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: imgIndex === (item.images.length - 1) ? 'default' : 'pointer', opacity: imgIndex === (item.images.length - 1) ? 0.3 : 1 }}
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index, imgIndex)}
                      aria-label="Remove photo"
                      style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {(item.images || []).length < 5 && (
                  <label style={{ width: '80px', height: '80px', border: '2px dashed var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--textSecondary)', fontSize: '11px', gap: '4px' }}>
                    <ImageIcon size={20} />
                    Add
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => { handleAddImages(index, e.target.files); e.target.value = ''; }}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <h4 style={{ marginBottom: '12px' }}>Package Dimensions (for shipping)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Weight (lbs)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={item.packageDimensions.weight}
                    onChange={(e) => handleItemChange(index, 'packageDimensions.weight', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Length (in)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={item.packageDimensions.length}
                    onChange={(e) => handleItemChange(index, 'packageDimensions.length', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Width (in)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={item.packageDimensions.width}
                    onChange={(e) => handleItemChange(index, 'packageDimensions.width', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Height (in)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={item.packageDimensions.height}
                    onChange={(e) => handleItemChange(index, 'packageDimensions.height', e.target.value)}
                    className="input"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleAddItem}
            className="btn btn-outline-primary"
          >
            <Plus size={16} style={{ marginRight: '4px' }} />
            Add Another Item
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ padding: '12px 24px' }}
          >
            {loading ? 'Uploading & creating…' : `Create ${items.length} Item${items.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BulkCreateListings;
